import { Routes } from '@/core/routes'
import { FC } from 'react'
import {
  DesktopOrTablet,
  MediaContextProvider,
  Mobile
} from '@/components/atoms/media'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import { useUser } from '@/providers/userProvider'
import { Box, Flex } from '@/components/atoms/Grid'
import { useRouter } from 'next/router'
import { validFor } from '@/theme/validFor'
import MobileHeaderButtonRedesign from '@/components/molecules/mobileHeaderButtonRedesign'
import DesktopHeaderButtonRedesign from '@/components/molecules/desktopHeaderButtonRedesign'
import LoginDropdownRedesign from '@/components/molecules/loginDropdownRedesign'
import { UserIconRedesignDefault } from '@/components/atoms/icons/UserIconRedesignDefault'
import ListLoginButtonRedesign from '@/components/molecules/listLoginButtonRedesign'

export enum LoginButtonTypeRedesign {
  headerButton,
  listButton
}

type LoginButtonPropsRedesign = {
  type?: LoginButtonTypeRedesign
} & Rest

const LoginButtonRedesign: FC<LoginButtonPropsRedesign> = ({
  type = LoginButtonTypeRedesign.headerButton,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const { user, isLoggedIn } = useUser()
  const isSigned = isLoggedIn && !!user?.id
  const router = useRouter()
  const route = isSigned
    ? Routes.orderHistory
    : `${Routes.login}/?referer=${encodeURI(router.asPath)}`

  return type === LoginButtonTypeRedesign.headerButton ? (
    <StyledBox>
      <MediaContextProvider>
        <DesktopOrTablet>
          <Flex maxWidth={200} data-testid="headerLoginButton" {...rest}>
            <DesktopHeaderButtonRedesign
              hrefLink={route}
              title={user?.firstname || t('Tower.Toolbar.Buttons.login')}
              IconElement={
                <UserIconRedesignDefault
                  color="#242424"
                  width={24}
                  height={24}
                />
              }
              data-testid={isSigned}
              p={0}
              isDropdown
            />
          </Flex>
        </DesktopOrTablet>

        <Mobile>
          <MobileHeaderButtonRedesign
            data-testid={route}
            hrefLink={route}
            title={user?.firstname || t('Tower.Toolbar.Buttons.login')}
            IconElement={
              <UserIconRedesignDefault
                ccolor="#242424"
                width={20}
                height={20}
              />
            }
            {...rest}
          />
        </Mobile>
      </MediaContextProvider>
      <StyledLoginDropdown />
    </StyledBox>
  ) : (
    <div data-testid="mobileLoginMenuButton" {...rest}>
      <ListLoginButtonRedesign
        hrefLink={route}
        title={user?.firstname || t('Login.buttonTitle')}
        isSigned={isSigned}
      />
    </div>
  )
}

export default LoginButtonRedesign

const StyledLoginDropdown = styled(LoginDropdownRedesign)`
  max-height: 100%;
  opacity: 0;
  visibility: hidden;
  -webkit-transition: opacity 0.3s 0.2s, visibility 0s 0.2s;
  -o-transition: opacity 0.3s 0.2s, visibility 0s 0.2s;
  transition: opacity 0.3s 0.2s, visibility 0s 0.2s;
`

const StyledBox = styled(Box)`
  position: relative;

  &:hover {
    ${StyledLoginDropdown} {
      opacity: 1;
      visibility: visible;
      transition-delay: 0s, 0s;
    }
  }
  ${validFor.mobile`
    &:hover {
      ${StyledLoginDropdown} {
      opacity: 0;
      visibility: none;
    }}
  `}
`
